import { useMutation } from '@tanstack/react-query';
import { readAllNotifications } from 'api/services';
import { Divider } from 'library/divider';
import { FloatingPortal, useFloatingElement } from 'library/floating';
import { BellIcon, CrossIcon, DoubleCheckIcon } from 'library/icons';
import { CircularProgress } from 'library/loaders';
import { useDesktopMediaQuery } from 'library/media-query';
import ShadowScrollContainer from 'modules/shared/components/helpers/ShadowScrollContainer';
import useBoundingClientRect from 'modules/shared/hooks/useBoundingClientRect';
import { getViewportHeight } from 'modules/shared/utils/screen.utils';
import { FC, useCallback, useEffect, useMemo } from 'react';
import ScrollLock from 'react-scrolllock';
import useNotificationData from '../../hooks/useNotificationData';
import NotificationGroup from '../NotificationGroup/NotificationGroup';
import NotificationLoading from '../NotificationLoading/NotificationLoading';
import { CloudIllustration } from 'modules/shared/features/illustration';

interface NotificationBarProps {}

const NotificationBar: FC<NotificationBarProps> = () => {
    const { context, refs, getReferenceProps, getFloatingProps } = useFloatingElement({
        placement: 'bottom-end',
        autoMaxWidth: false,
        autoMaxHeight: false,
    });

    const readAllNotificationsMutation = useMutation(() => readAllNotifications());

    const [headerRefSetter, headerBoundingClientRect] = useBoundingClientRect();
    const [footerRefSetter, footerBoundingClientRect] = useBoundingClientRect();

    const isDesktop = useDesktopMediaQuery();

    const {
        unreadNotificationCount,
        groupedNotifications,
        isLoadingNotifications,
        refetchQueries,
        isLoadingMoreNotifications,
        loadMoreNotifications,
        updateReadAll,
    } = useNotificationData();

    useEffect(() => {
        refetchQueries();
    }, [context.open, refetchQueries]);

    const scrollContainerHeight = useMemo(() => {
        if (isDesktop) return 440;

        return getViewportHeight() - headerBoundingClientRect.height - footerBoundingClientRect.height;
    }, [footerBoundingClientRect.height, headerBoundingClientRect.height, isDesktop]);

    const handleScroll = useCallback(
        (e: any) => {
            if (e.target.clientHeight >= e.target.scrollHeight - e.target.scrollTop - 10) {
                if (isLoadingMoreNotifications) return;

                loadMoreNotifications();
            }
        },
        [isLoadingMoreNotifications, loadMoreNotifications]
    );

    const handleReadAllNotifications = useCallback(async () => {
        updateReadAll();

        await readAllNotificationsMutation.mutateAsync();
    }, [readAllNotificationsMutation, updateReadAll]);

    return (
        <div className="flex items-center relative">
            <button {...getReferenceProps()} ref={refs.setReference} className="relative" type="button">
                {unreadNotificationCount > 0 && (
                    <span className="absolute inline-flex justify-center items-center w-5 h-5 bg-[#EF7C11] rounded-full top-[-9px] right-[-8px] text-[10px] font-semibold text-white">
                        {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                    </span>
                )}
                <BellIcon />
            </button>

            {context.open && (
                <FloatingPortal root={document.getElementById('dropdown-root')}>
                    <div
                        {...getFloatingProps()}
                        ref={refs.setFloating}
                        style={isDesktop ? context.floatingStyles : { position: 'fixed', inset: 0 }}
                        className="flex flex-col floating-menu md:rounded-lg w-full md:w-[380px] z-[19] py-4"
                    >
                        <div ref={headerRefSetter} className="flex items-center justify-between px-4 pb-4">
                            <h5>Your notifications</h5>

                            <button
                                className="btn-action text-grayscale-400"
                                onClick={() => context.onOpenChange(false)}
                            >
                                <CrossIcon width={10} height={10} />
                            </button>
                        </div>

                        <ScrollLock>
                            <ShadowScrollContainer
                                onScroll={handleScroll}
                                maxHeight={scrollContainerHeight}
                                className="divide-y divide-separator-color-light"
                            >
                                {groupedNotifications.map((row, i) => (
                                    <NotificationGroup
                                        key={i}
                                        index={i}
                                        title={row.key}
                                        notifications={row.list}
                                        onClose={() => context.onOpenChange(false)}
                                    />
                                ))}

                                {(isLoadingNotifications || isLoadingMoreNotifications) && <NotificationLoading />}
                            </ShadowScrollContainer>
                        </ScrollLock>

                        {!isLoadingNotifications && groupedNotifications.length === 0 && (
                            <div className="flex flex-col items-center text-center py-8">
                                <CloudIllustration width={132} height={100} />
                                <div className="font-semibold mt-4">No notifications yet</div>
                                <div className="text-sm text-grayscale-500 mt-1">
                                    We will notify you once you have one.
                                </div>
                            </div>
                        )}

                        <Divider />

                        <div ref={footerRefSetter} className="flex items-center justify-between px-4 pt-4">
                            {/* <button className="flex items-center gap-1">
                                <SettingsIcon width={16} height={16} className="text-grayscale-500" />
                                <div className="text-xs text-grayscale-600 font-semibold">Manage notifications</div>
                            </button> */}

                            <button
                                className="ml-auto flex items-center gap-1 text-primary"
                                onClick={handleReadAllNotifications}
                            >
                                <DoubleCheckIcon width={20} height={20} className="text-primary" />

                                <div className="text-xs font-semibold">Mark all as read</div>

                                {readAllNotificationsMutation.isLoading && <CircularProgress size={18} />}
                            </button>
                        </div>
                    </div>
                </FloatingPortal>
            )}
        </div>
    );
};

export default NotificationBar;
